import React, { forwardRef, useEffect, useImperativeHandle } from "react";

import BaseCard from "../../../components/ui/BaseCard";
import { useDispatch, useSelector } from "react-redux";
import {
  removeWorkOrderService,
  updateWorkOrderForm,
  updateWorkOrderFormValidation,
} from "../../../store/workOrder/workOrderSlice";
import { useQuery } from "@tanstack/react-query";
import {
  fetchCategories,
  fetchSubCategories,
  fetchVendors,
} from "../../../services/api/category";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";

const WorkExperienceForm = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const workExperience = useSelector(
    (state) => state.workOrder.summary.workExperience
  );
  const workOrderFormValidation = useSelector(
    (state) => state.workOrder.workOrderFormValidation
  );
  const workExperienceData = props.workExperienceData
  const {
    isLoading: IsCategoriesLoading,
    error: isCategoriesError,
    data: categoriesData,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const {
    isLoading: IsVendorsLoading,
    error: isVendorsError,
    data: vendorsData,
  } = useQuery({
    queryKey: ["vendorsData", workExperience?.categoryId], // Add categoryId to the queryKey
    queryFn: () => fetchVendors(workExperience?.categoryId),
    enabled: !!workExperience?.categoryId,
  });

  const {
    isLoading: IsSubCategoriesLoading,
    error: isSubCategoriesError,
    data: subCategoriesData,
  } = useQuery({
    queryKey: ["subCategories", workExperience?.vendorId], // Add vendorId to the queryKey
    queryFn: () => fetchSubCategories(workExperience?.vendorId),
    enabled: !!workExperience?.vendorId,
  });

  useImperativeHandle(ref, () => ({
    // Collects every failure instead of bailing on the first, so the user isn't
    // walked through them one Next click at a time.
    isValid: () => {
      const errors = [];

      const fail = (fieldName, message) => {
        errors.push(message);
        dispatch(updateWorkOrderFormValidation({ fieldName, data: true }));
      };

      if (!workExperience?.categoryId) {
        fail("categoryId", "Technology Stacks are required");
      }
      if (!workExperience?.vendorId) {
        fail("vendorId", "Category is required");
      }
      if (!workExperience?.workOrderServices?.length) {
        fail("workOrderServices", "Skills are required");
      }

      return { valid: errors.length === 0, errors };
    },
  }));

  const formattedCategories = categoriesData?.data?.map((category) => ({
    label: category.category,
    value: category.id,
  }));

  const formattedSubCategories = subCategoriesData?.data?.map(
    (subCategory) => ({
      label: subCategory.subCategory,
      value: subCategory.id,
    })
  );

  const formattedVendors = vendorsData?.data?.map((vendor) => ({
    label: vendor.name,
    value: vendor.id,
  }));
  useEffect(() => {
    if (workExperienceData) {
      const hydratedWorkExperience = {
        categoryId: workExperienceData.id,
        vendorId: workExperienceData.vendor.id,
        workOrderServices: workExperienceData.vendor.services.map((s) => s.id),
      };

      dispatch(
        updateWorkOrderForm({
          step: "summary",
          data: { workExperience: hydratedWorkExperience },
        })
      );
      // Baseline for the edit modal's Save Changes button — see useSectionChanges.
      props.onHydrate?.(hydratedWorkExperience);
    }
  }, [dispatch, workExperienceData]);

  /* useEffect(() => {
    console.log("running");
    dispatch(
      updateWorkOrderForm({
        step: "summary",
        data: {
          workExperience: {
            workOrderServices: [],
          },
        },
      })
    );
  }, [workExperience?.vendorId, workExperience?.categoryId]); */

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;

    if (
      (name === "vendorId" && workExperience.vendorId !== value) ||
      (name === "categoryId" && workExperience.categoryId !== value)
    ) {
      dispatch(
        updateWorkOrderForm({
          step: "summary",
          data: {
            workExperience: {
              vendorId: null,
              workOrderServices: [],
            },
          },
        })
      );
    }
    // dispatch(removeWorkOrderService(value));
    dispatch(
      updateWorkOrderForm({
        step: "summary",
        data: {
          workExperience: {
            [name]: value,
          },
        },
      })
    );

    if (name === "categoryId" && value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "categoryId",
          data: false,
        })
      );
    } else if (name === "categoryId" && !value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "categoryId",
          data: true,
        })
      );
    }
    if (name === "vendorId" && value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "vendorId",
          data: false,
        })
      );
    } else if (name === "vendorId" && !value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "vendorId",
          data: true,
        })
      );
    }

    if (name === "workOrderServices" && value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "workOrderServices",
          data: false,
        })
      );
    } else if (name === "workOrderServices" && !value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "workOrderServices",
          data: true,
        })
      );
    }
  };

  console.log("services", workExperience);

  return (
    <BaseCard
      className="pt-4 pb-10 lg:px-16 px-5 mt-2 rounded-lg"
      elevation={1}
    >
      <div className="border-bottom mb-4 flex justify-between pb-3">
        <h6 className="text-dark">Work Experience</h6>
      </div>
      <div className="grid grid-cols-2 gap-y-4">
        <div className="lg:mr-10 lg:col-span-1 col-span-2">
          <BaseAutoSelect
            error={workOrderFormValidation?.categoryId}
            errorMsg="Technology Stacks are required"
            placeholder="Select type of work..."
            fullHeight={false}
            requirement="required"
            labelShrink={false}
            label="Technology Stacks"
            options={formattedCategories}
            multiple={false}
            name="categoryId"
            isDefaultField={false}
            value={workExperience?.categoryId || null}
            onChange={handleChange}
          />
        </div>

        <div className="lg:col-span-1 col-span-2">
          <BaseAutoSelect
            error={workOrderFormValidation?.vendorId}
            errorMsg="Category is required"
            className=""
            labelShrink={false}
            label="Category"
            placeholder="Add additional types of work..."
            fullHeight={false}
            options={formattedVendors}
            requirement="required"
            multiple={false}
            name="vendorId"
            value={workExperience?.vendorId || null}
            isDefaultField={false}
            onChange={handleChange}
          />
        </div>

        <div className="lg:mr-10 lg:col-span-1 col-span-2">
          <BaseAutoSelect
            error={workOrderFormValidation?.workOrderServices}
            errorMsg="Please select at least 1 skill"
            disableCloseOnSelect
            infoIcon={false}
            label="Skills"
            fullHeight={false}
            labelShrink={false}
            requirement="required"
            placeholder="Add services..."
            options={formattedSubCategories}
            name="workOrderServices"
            multiple
            value={workExperience?.workOrderServices || []}
            isDefaultField={false}
            onChange={handleChange}
          />
        </div>
      </div>
    </BaseCard>
  );
});

export default WorkExperienceForm;
